import { memo, useCallback } from 'react'

import { ControlPointDuplicate } from '@mui/icons-material'
import { MenuItem, ListItemIcon, Skeleton, Typography, Divider } from '@mui/material'
import { MediaPlatform } from '@nativewaves/nw-cloud-sdk/media'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { MenuItemCopyId, MenuItemDelete } from '@shared/components/MaterialUIEnhancements/Menu/MenuItem'
import { RowActionsProps } from '@shared/components/Table'
import { useMediaLibraryCreateMutation, useMediaLibraryDeleteMutation } from 'hooks/mutations/publishing'

const RowActionsRaw = ({ row, onClose }: RowActionsProps<MediaPlatform>) => {
  const { t } = useTranslation(['common', 'entity', 'domain'])
  const { enqueueSnackbar } = useSnackbar()

  const { mutate: createMediaLibrary, ...mediaLibraryCreateMutation } = useMediaLibraryCreateMutation()
  const { mutate: deleteMediaLibrary } = useMediaLibraryDeleteMutation()

  const handleDuplicateMediaLibrary = useCallback(() => {
    createMediaLibrary(
      {
        mediaPlatformInsert: {
          ...row.original,
          defaultWorkspaceId: row.original.defaultWorkspaceId || '',
          name: `${row.original.name} (${t('duplicate')})`,
        },
      },
      {
        onSuccess: () => {
          onClose()
        },
      },
    )
  }, [createMediaLibrary, onClose, row.original, t])

  const handleDeleteMediaLibrary = useCallback(() => {
    onClose()

    deleteMediaLibrary(
      { libraryId: row.original.id },
      {
        onSuccess: () =>
          enqueueSnackbar(t('domain:MediaLibrary.nameDelete', { name: row.original.name }), { variant: 'success' }),
      },
    )
  }, [deleteMediaLibrary, enqueueSnackbar, onClose, row.original.id, row.original.name, t])

  return (
    <>
      <MenuItem onClick={handleDuplicateMediaLibrary} disabled={mediaLibraryCreateMutation.isPending}>
        <ListItemIcon>
          <ControlPointDuplicate />
        </ListItemIcon>
        <Typography sx={{ width: '100%' }}>
          {mediaLibraryCreateMutation.isPending ? <Skeleton variant="text" /> : t('duplicate')}
        </Typography>
      </MenuItem>
      <MenuItemCopyId onSuccess={onClose} id={row.original.id} />
      <Divider sx={{ my: 1 }} component="li" />
      <MenuItemDelete onConfirm={handleDeleteMediaLibrary} />
    </>
  )
}

export const RowActions = memo(RowActionsRaw)
